import { useState, useEffect, useMemo } from 'react';
import * as yup from 'yup';
import styled, { css } from 'styled-components';
import { useDispatch } from 'react-redux';
import { affiliateActions } from '@figure/frontlib-onboarding-redux';
import { dateFormatter, dateUtil } from '@figure/frontlib-util';
import differenceInYears from 'date-fns/difference_in_years';
import { useIdentity, usePersonalLoan, useProfile, useRedux, useRedirect } from 'shared/hooks';
import { isFigure } from 'shared/utils/ThemeHelper';
import {
  SPACINGS,
  HLink,
  P,
  cssByBreakpoint,
  BREAKPOINTS,
  TYPOGRAPHY,
  COLORS,
} from '@figure/figure-frontlib-gui';
import {
  Financing,
  Profile,
  AgeRestriction,
  LivingArrangements,
  Citizenship,
} from 'shared/constants';
import { SUFFIX_TYPE } from 'shared/constants/profile';
import { isProd } from 'shared/utils';
import { sanitizePhoneNumber, reduceFormElementsToKeyOrValue } from './utils';

const Disclaimer = styled(P)`
  flex: 1 1 100%;
  ${TYPOGRAPHY.S};
  text-align: center;
`;

const CreateAccountDisclaimer = (props) => (
  <Disclaimer {...props}>
    By clicking 'Check my rate,' I understand I am creating an account with Figure and agree to the{' '}
    <HLink
      $css={css`
        ${TYPOGRAPHY.S};
        color: ${COLORS.PRIMARY_3};
      `}
      href="/terms"
    >
      terms
    </HLink>
    ,{' '}
    <HLink
      $css={css`
        ${TYPOGRAPHY.S};
        color: ${COLORS.PRIMARY_3};
      `}
      href="/communications"
    >
      electronic communications policy
    </HLink>
    , and{' '}
    <HLink
      $css={css`
        ${TYPOGRAPHY.S};
        color: ${COLORS.PRIMARY_3};
      `}
      href="/privacy"
    >
      privacy policy
    </HLink>
    .
  </Disclaimer>
);

const AlreadyHaveAndAccountLink = () => {
  const { redirectToLocation } = useRedirect();

  return (
    <P
      $css={css`
        flex: 1 1 100%;
        text-align: center;
        margin-bottom: ${SPACINGS.L}px;
      `}
    >
      Already have an account? Log in{' '}
      <HLink
        onClick={() =>
          redirectToLocation(
            `${process.env.REACT_APP_LOGIN_PATH}?redirectUrl=${encodeURIComponent(
              window.location.pathname
            )}`
          )
        }
      >
        here
      </HLink>
    </P>
  );
};

const HelperText = styled(P)`
  ${TYPOGRAPHY.S_SEMIBOLD};
  color: ${COLORS.NEUTRAL_450};
  flex: 1 1 100%;
  display: flex;
  gap: ${SPACINGS.S}px;

  ${cssByBreakpoint(
    [
      css`
        justify-content: center;
        margin-bottom: ${SPACINGS.L}px;
      `,
      css`
        justify-content: flex-start;
        margin-bottom: 0;
      `,
    ],
    [BREAKPOINTS.SM, BREAKPOINTS.MD]
  )}

  svg {
    margin-top: ${SPACINGS.XS}px;
    color: ${COLORS.NEUTRAL_450};
  }
`;

const AFFILIATE_COOKIES = [
  {
    key: '_ft_prepop',
    action: 'getAffiliateOfferPrepopData',
  },
];
const useSetAffiliateData = () => {
  const dispatch = useDispatch();
  const { userData: affiliatePrePopulatedData, ...prepopActions } = useRedux(
    'affiliateReducer',
    affiliateActions
  );
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (isFigure) {
      const configs = AFFILIATE_COOKIES.filter(({ key }) => document.cookie.includes(key));
      if (configs.length !== 0) {
        Promise.all(
          configs.map(({ affiliateType = '', action }) => dispatch(prepopActions[action]))
        ).finally(() => setIsInitialized(true));
      } else {
        setIsInitialized(true);
      }
    } else {
      setIsInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isInitialized, affiliatePrePopulatedData };
};

export const useBasicInfoAndCreateAccountSteps = () => {
  // TODO: set this to not return steps until affiliate data has been fetched. and then change form to not enable reinit.
  const { isAuthenticated } = useIdentity();
  const {
    isInitialized: isAffiliateDataInitialized,
    affiliatePrePopulatedData,
  } = useSetAffiliateData();
  const { personalLoan } = usePersonalLoan(false);
  const { profile } = useProfile(false);

  // redirect for application start 
  useEffect(() => {
    window.location.href = 'https://www.figure.com';
  }, []);

  const steps = useMemo(
    () => [
      // STEP 1
      {
        path: 'loan-amount',
        header: 'Approval in minutes, and funding the next day',
        nextButtonText: 'Continue',
        formElements: [
          ...(!isAuthenticated
            ? [
                {
                  formElementComponentType: 'content',
                  content: <AlreadyHaveAndAccountLink />,
                },
              ]
            : []),
          {
            name: 'requestedAmount',
            initialValue:
              personalLoan.desiredLoanAmount || affiliatePrePopulatedData.requestedAmount || null,
            formElementComponentType: 'input',
            props: {
              label: 'Requested Loan Amount',
              helperText: 'Max Loan Amount $50,000',
              type: 'currency',
              maxLength: 6, // 5 digits plus the comma
            },
            validation: yup
              .number()
              .typeError('Enter an amount')
              .required('Required')
              .min(5000, 'Amount must be at least $5000')
              .max(50000, 'Amount must be less than or equal to $50,000'),
          },
          {
            name: 'financingPurpose',
            initialValue: personalLoan.reason || '',
            formElementComponentType: 'select',
            options: Financing.PURPOSES,
            props: {
              label: 'Financing Purpose',
            },
            validation: yup.string().required('Required'),
          },
          {
            formElementComponentType: 'content',
            content: <HelperText>This will not affect your credit score.</HelperText>,
          },
        ],
      },

      // STEP 2
      {
        path: 'personal-info',
        header: 'Tell us about yourself',
        nextButtonText: 'Continue',
        formElements: [
          {
            name: 'dob',
            initialValue:
              dateFormatter.formatDisplayLocalDate(profile.dob) ||
              dateFormatter.formatDisplayLocalDate(affiliatePrePopulatedData.dob) ||
              '',
            formElementComponentType: 'input',
            props: {
              label: 'Date of Birth',
              type: 'date',
              className: 'fs-mask',
            },
            validation: yup
              .string()
              .required('Required')
              .test('is-a-valid-date', 'DOB must be MM/DD/YYYY', (value) =>
                dateUtil.dateIsValid(value)
              )
              .test('is-not-age-restricted', 'Must be $1 years or older', function (value) {
                const state = this.parent.propertyAddress.state;
                const dob = value;
                const isInAgeRestrictedState = !!AgeRestriction.AGE_BY_STATE[state] ?? false;
                const stateRestrictedAge = AgeRestriction.AGE_BY_STATE?.[state];
                const applicantsAge = differenceInYears(new Date(), dob);

                if (isInAgeRestrictedState && applicantsAge < stateRestrictedAge) {
                  return this.createError({
                    message: `Must be ${stateRestrictedAge} years or older`,
                  });
                } else {
                  return true;
                }
              })
              .test('is-at-last-eighteen', 'Must be 18 years or older', (value) =>
                dateUtil.isEighteen(value)
              )
              .test('is-at-least-1900', 'Must be at least 1900', (value) =>
                dateUtil.isAtLeast1900(value)
              ),
          },
          {
            name: 'citizenship',
            initialValue: profile.citizenship || '',
            formElementComponentType: 'select',
            options: Citizenship.CITIZENSHIP_TYPE,
            props: {
              label: 'Citizenship',
            },
            validation: yup
              .string()
              .required('Required')
              .test(
                'is-eligible',
                'Borrower must be a U.S. citizen or Permanent Resident with 10-year non-conditional Permanent Resident card',
                (value) => value === 'CITIZEN' || value === 'PERMANENT_RESIDENT'
              ),
          },
          {
            name: 'phone',
            initialValue:
              sanitizePhoneNumber(
                profile.phoneNumbers[0]?.number || affiliatePrePopulatedData.phoneNumber
              ) || '',
            formElementComponentType: 'input',
            props: {
              label: 'Mobile Phone Number',
              type: 'tel',
              prefixDisplay: '+1',
              helperText:
                'Phone number will be used for communications related to your application or account.',
            },
            validation: yup
              .string()
              .required('Required')
              .test(
                'is-phone-number',
                'Must be a valid phone number',
                (value) => !value || value.toString().length === 10
              )
              .test(
                'is_pos_one_not_one',
                'Do not enter “1” as first digit of area code',
                (value) => value?.toString()[0] !== '1'
              ),
          },
          {
            formElementComponentType: 'spacer',
          },
          {
            name: 'callAndTextCommunicationConsent',
            initialValue: true,
            formElementComponentType: 'checkbox',
            isNotShown: !isFigure,
            props: {
              label:
                'Want to receive text updates about other products or services Figure or its affiliates may offer? You are providing Figure your express written consent to communicate with you via direct and automated calls and text messages about other products and services.',
            },
          },
        ],
      },

      // STEP 3
      {
        path: 'living-arrangement',
        header: 'Your current living arrangement',
        nextButtonText: 'Continue',
        formElements: [
          {
            name: 'livingArrangement',
            initialValue: personalLoan.currentLivingArrangement || '',
            formElementComponentType: 'cardSelect',
            options: LivingArrangements.TYPES,
            validation: yup.string().required('Required'),
          },
        ],
      },

      // STEP 4
      {
        path: 'address',
        header: 'Primary address',
        nextButtonText: 'Continue',
        formElements: [
          {
            name: 'propertyAddress',
            formElementComponentType: 'address',
            initialValue: (() => {
              const profileAddress = profile.addresses.find(
                ({ typeDescription }) => typeDescription === 'Primary residence'
              );
              return {
                uuid: profileAddress?.uuid || affiliatePrePopulatedData?.address?.uuid || '',
                street1:
                  profileAddress?.street1 || affiliatePrePopulatedData?.address?.street1 || '',
                street2:
                  profileAddress?.street2 || affiliatePrePopulatedData?.address?.street2 || '',
                city: profileAddress?.city || affiliatePrePopulatedData?.address?.city || '',
                state: profileAddress?.state || affiliatePrePopulatedData?.address?.state || '',
                zip: profileAddress?.zip || affiliatePrePopulatedData?.address?.zip || '',
              };
            })(),
            props: { label: 'Address', className: 'fs-mask' },
            validation: yup.object().shape({
              street1: yup.string().trim().required('Required'),
              city: yup.string().trim().required('Required'),
              state: yup
                .string()
                .typeError('Enter a valid state')
                .length(2, 'Enter a valid state')
                .required('Required'),
              zip: yup
                .string()
                .required('Required')
                .min(5, 'Must be at least 5 digits')
                .max(10, 'Must be less than 10 digits')
                .test(
                  'is-a-valid-zip',
                  'Must be a valid zip code',
                  (value) => !value || /^[0-9]{5}(?:-[0-9]{4})?$/.test(value)
                ),
            }),
          },
        ],
      },

      // STEP 5
      {
        path: 'employment-status',
        header: 'Your current employment status',
        nextButtonText: 'Continue',
        formElements: [
          {
            name: 'employmentType',
            initialValue: personalLoan?.incomeSources?.[0]?.employmentType || '',
            formElementComponentType: 'cardSelect',
            options: Profile.EMPLOYMENT_TYPE,
            isColumnLayout: true,
            validation: yup
              .string()
              .required('Required')
              .test(
                'is-unemployed',
                "We’re unable to lend to unemployed applicants. However, if you rely on income that is considered community or marital income in your state, we can use your spouse or partner's employment to evaluate your application. If you’d like to use this option, please select their employment status above.",
                (value) => value !== Profile.UNEMPLOYED
              ),
          },
        ],
      },

      // STEP 6
      {
        path: 'income',
        header: 'Annual gross income including bonus',
        nextButtonText: 'I agree',
        formElements: [
          {
            name: 'totalIncome',
            initialValue:
              personalLoan.income?.incomePrimary || affiliatePrePopulatedData.income || null,
            formElementComponentType: 'input',
            props: {
              placeholder: ' ',
              type: 'currency',
              helperText: 'Salary or income received by you individually',
            },
            validation: yup
              .number()
              .typeError('Enter an amount')
              .required('Required')
              .min(1, 'Must be at least $1')
              .max(9999999, 'Must be less than $10,000,000'),
          },
          {
            name: 'creditProfileAndIncomeVerificationConsent',
            initialValue: false,
            formElementComponentType: 'checkbox',
            props: {
              label: (
                <div>
                  I agree to the{' '}
                  <a href="/credit-profile-and-income-verification-authorization/" target="_blank">
                    Credit Profile and Income Verification Authorization.
                  </a>
                </div>
              ),
            },
            validation: yup.boolean().oneOf([true], 'Required'),
          },
        ],
      },

      // STEP 7: Create account, or if account exists but name does not then collect name, else step is skipped.
      ...(isAuthenticated
        ? !profile.name?.firstName || !profile.name?.lastName
          ? [
              {
                path: 'create-account',
                header: 'Update Information',
                nextButtonText: 'Check my rate',
                formElements: [
                  {
                    name: 'nameGroup',
                    formElementComponentType: 'group',
                    formElements: [
                      {
                        name: 'firstName',
                        initialValue:
                          profile?.name?.firstName ||
                          affiliatePrePopulatedData?.name?.firstName ||
                          '',
                        formElementComponentType: 'input',
                        props: {
                          label: 'First Name',
                          className: 'fs-mask',
                        },
                        validation: yup.string().required(),
                      },
                      {
                        name: 'lastName',
                        initialValue:
                          profile?.name?.lastName ||
                          affiliatePrePopulatedData?.name?.lastName ||
                          '',
                        formElementComponentType: 'input',
                        props: {
                          label: 'Last Name',
                          className: 'fs-mask',
                        },
                        validation: yup.string().required(),
                      },
                      {
                        name: 'suffix',
                        initialValue:
                          profile?.name?.suffix || affiliatePrePopulatedData?.name?.suffix || '',
                        formElementComponentType: 'select',
                        options: SUFFIX_TYPE,
                        props: {
                          label: 'Suffix',
                        },
                      },
                    ],
                  },
                ],
              },
            ]
          : []
        : [
            {
              path: 'create-account',
              header: 'Create an account',
              nextButtonText: 'Check my rate',
              formElements: [
                {
                  formElementComponentType: 'content',
                  content: <AlreadyHaveAndAccountLink />,
                },
                {
                  name: 'nameGroup',
                  formElementComponentType: 'group',
                  formElements: [
                    {
                      name: 'firstName',
                      initialValue: affiliatePrePopulatedData?.name?.firstName || '',
                      formElementComponentType: 'input',
                      props: {
                        label: 'First Name',
                        className: 'fs-mask',
                      },
                      validation: yup.string().required(),
                    },
                    {
                      name: 'lastName',
                      initialValue: affiliatePrePopulatedData?.name?.lastName || '',
                      formElementComponentType: 'input',
                      props: {
                        label: 'Last Name',
                        className: 'fs-mask',
                      },
                      validation: yup.string().required(),
                    },
                    {
                      name: 'suffix',
                      initialValue: affiliatePrePopulatedData?.name?.suffix || '',
                      formElementComponentType: 'select',
                      options: SUFFIX_TYPE,
                      props: {
                        label: 'Suffix',
                      },
                    },
                  ],
                },
                {
                  name: 'email',
                  initialValue: affiliatePrePopulatedData?.email || '',
                  formElementComponentType: 'input',
                  validation: yup
                    .string()
                    .email('Must be a valid email')
                    .required()
                    .test(
                      'is-not-figure',
                      'Figure email addresses are not allowed',
                      (value) => !isProd() || value?.toLowerCase().indexOf('@figure.') === -1
                    ),
                  isFullWidth: true,
                  props: {
                    label: 'Email Address',
                    type: 'email',
                  },
                },
                {
                  name: 'password',
                  initialValue: '',
                  formElementComponentType: 'passwordInput',
                  props: {
                    label: 'Choose a password to save your progress',
                    strengthCheckField: 'strengthCheck',
                  },
                  validation: yup
                    .string()
                    .required()
                    .test(
                      'is-strong',
                      'Password is not strong enough',
                      (value, context) => context.parent?.strengthCheck >= 3
                    ),
                },
                {
                  name: 'confirmPassword',
                  initialValue: '',
                  formElementComponentType: 'passwordInput',
                  props: {
                    label: 'Confirm Password',
                    useStrengthCheck: false,
                  },
                  validation: yup
                    .string()
                    .required()
                    .test(
                      'is-same',
                      'Passwords must match',
                      (value, context) => value === context.parent.password
                    ),
                },
                {
                  formElementComponentType: 'content',
                  content: <CreateAccountDisclaimer />,
                },
              ],
            },
          ]),
    ],
    [
      affiliatePrePopulatedData?.address?.city,
      affiliatePrePopulatedData?.address?.state,
      affiliatePrePopulatedData?.address?.street1,
      affiliatePrePopulatedData?.address?.street2,
      affiliatePrePopulatedData?.address?.uuid,
      affiliatePrePopulatedData?.address?.zip,
      affiliatePrePopulatedData.dob,
      affiliatePrePopulatedData?.email,
      affiliatePrePopulatedData.income,
      affiliatePrePopulatedData?.name?.firstName,
      affiliatePrePopulatedData?.name?.lastName,
      affiliatePrePopulatedData?.name?.suffix,
      affiliatePrePopulatedData.phoneNumber,
      affiliatePrePopulatedData.requestedAmount,
      isAuthenticated,
      personalLoan.currentLivingArrangement,
      personalLoan.desiredLoanAmount,
      personalLoan.income?.incomePrimary,
      personalLoan?.incomeSources,
      personalLoan.reason,
      profile.addresses,
      profile.citizenship,
      profile.dob,
      profile.name?.firstName,
      profile.name?.lastName,
      profile.name?.suffix,
      profile.phoneNumbers,
    ]
  );

  const validationSchema = yup
    .object()
    .shape(reduceFormElementsToKeyOrValue({ items: steps, key: 'validation' }));
  const initialValues = {
    ...reduceFormElementsToKeyOrValue({ items: steps, key: 'initialValue' }),
    strengthCheck: 0,
  };

  const isInitialized = isAffiliateDataInitialized;

  return { steps, validationSchema, initialValues, isInitialized };
};
